import { APP_BASE_HREF } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  CSP_NONCE,
  EnvironmentInjector,
  ErrorHandler,
  inject,
  NgModule,
  provideAppInitializer,
  runInInjectionContext,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { GlobalErrorsHandler } from '@shared/errors/global-errors-handler';
import { BlobErrorHttpInterceptor } from '@shared/interceptors/blob-error-http-interceptor';
import { LogoutIfNotAuthenticatedInterceptor } from '@shared/interceptors/logout-if-not-authenticated-interceptor';
import { UrlEncodingInterceptor } from '@shared/interceptors/url-encoding.interceptor';
import { HydrationEffects } from '@shared/ngrx-meta-reducer/hydration/hydration.effects';
import { metaReducers } from '@shared/ngrx-meta-reducer/ngrx-meta.functions';
import { SharedModule } from '@shared/shared.module';
import { createTranslateLoader } from '@shared/translate/custom-translation.loader';
import { keycloakBearerTokenConfig } from '@shared/util/keycloak/keycloak-bearer-token.config';
import { KeycloakUtils } from '@shared/util/keycloak/keycloak.utils';
import { KeycloakActionNotifierService } from '@shared/services/auth/keycloak-action-notifier.service';
import { ModalModule } from '@widgets/modal/modal.module';
import { SpinnerModule } from '@widgets/spinner/spinner.module';
import { ToastComponent } from '@widgets/toastr/toast.component';
import { environment } from 'environments/environment';
import {
  AutoRefreshTokenService,
  CUSTOM_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  customBearerTokenInterceptor,
  provideKeycloak,
  UserActivityService,
  withAutoRefreshToken,
} from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { CookieService } from 'ngx-cookie-service';
import { provideToastr } from 'ngx-toastr';
import { AppComponent } from './app.component';
import { FeatureFlagDebugPanelComponent } from '@shared/components/feature-flag-debug-panel/feature-flag-debug-panel.component';
import { BrandingConfigService } from '@shared/services/branding/branding-config.service';
import { HeapService } from '@shared/services/heap/heap.service';
import { ROUTES } from './app.routes';

const PUBLIC_AUTH_PAGES = ['auth/support', 'auth/passwordReset'];

const isPublicAuthPage = PUBLIC_AUTH_PAGES.some(page => window.location.pathname.includes(page));

@NgModule({
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    InlineSVGModule.forRoot({ baseUrl: '/' }),
    RouterModule.forRoot(ROUTES),
    StoreModule.forRoot({}, { metaReducers }),
    EffectsModule.forRoot([HydrationEffects]),
    ModalModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
      connectInZone: true,
    }),
    SharedModule,
    SpinnerModule,
    FeatureFlagDebugPanelComponent,
  ],
  declarations: [AppComponent],
  providers: [
    { provide: ErrorHandler, useClass: GlobalErrorsHandler },
    { provide: HTTP_INTERCEPTORS, useClass: LogoutIfNotAuthenticatedInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: UrlEncodingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: BlobErrorHttpInterceptor, multi: true },
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: 'googleTagManagerId', useValue: environment.googleTagManager },
    {
      provide: 'googleTagManagerCSPNonce',
      useFactory: (nonce: string) => nonce,
      deps: [CSP_NONCE],
    },
    provideKeycloak({
      config: {
        url: environment.keycloak.url,
        realm: environment.keycloak.realm,
        clientId: KeycloakUtils.getKeycloakClientId(),
      },
      // No initOptions: Keycloak is initialized below, after the branding config has been checked
      providers: [AutoRefreshTokenService, UserActivityService],
    }),
    // The cookie banner is not shown on INT/localhost, so Heap is loaded on startup there
    provideAppInitializer(() => {
      if (environment.int) {
        inject(HeapService).load();
      }
    }),
    provideAppInitializer(async () => {
      const keycloak = inject(Keycloak);
      const injector = inject(EnvironmentInjector);
      const continueStartup = await inject(BrandingConfigService).loadBrandingConfig();
      if (!continueStartup) {
        // Redirecting to the main portal; keep the bootstrap pending so Keycloak login is not started
        return new Promise<void>(() => undefined);
      }
      runInInjectionContext(injector, () =>
        withAutoRefreshToken({
          // 3 days in milliseconds: 3 * 24 * 60 * 60 * 1000
          sessionTimeout: 259200000,
        }).configure()
      );
      await keycloak
        .init({
          onLoad: isPublicAuthPage ? 'check-sso' : 'login-required',
          redirectUri: KeycloakUtils.getRedirectUri(),
          checkLoginIframe: false,
        })
        .catch(error => console.error('Keycloak initialization failed', error));
    }),
    provideAppInitializer(() => {
      const keycloak = inject(Keycloak);
      const notifier = inject(KeycloakActionNotifierService);
      const originalHook = keycloak.onActionUpdate;
      keycloak.onActionUpdate = (status, action) => {
        originalHook?.(status, action);
        if (action) {
          notifier.handle(action, status as 'success' | 'error' | 'cancelled');
        }
      };
    }),
    provideHttpClient(withInterceptors([customBearerTokenInterceptor]), withInterceptorsFromDi()),
    {
      provide: CUSTOM_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: keycloakBearerTokenConfig,
    },
    provideToastr({
      toastComponent: ToastComponent,
      timeOut: 3000,
      extendedTimeOut: 3000,
      positionClass: 'toast-bottom-center',
      closeButton: true,
      tapToDismiss: false,
    }),
    provideAnimations(),
    CookieService,
  ],
})
export class AppModule {}
